/**
 * Subscription Hook
 * 
 * Provides easy access to subscription features and checks
 */

import { useMemo } from 'react';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import {
  SubscriptionTier,
  hasFeature,
  getScansPerWeek,
  hasUnlimitedScans,
  GLOWLENS_FEATURES,
} from '../types/subscription';

export const useSubscription = () => {
  const { userProfile } = useAWSAuth();

  // Ensure tier is valid, default to 'plus' if invalid
  const rawTier = userProfile?.subscriptionTier;
  const tier: SubscriptionTier = (rawTier && rawTier in GLOWLENS_FEATURES) ? rawTier : 'plus';

  const features = useMemo(() => {
    // Double-check tier is valid (should already be validated above, but extra safety)
    const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
    return GLOWLENS_FEATURES[validTier];
  }, [tier]);

  const checkFeature = (feature: keyof typeof GLOWLENS_FEATURES.plus): boolean => {
    return hasFeature(tier, feature);
  };

  const scansAllowed = useMemo(() => {
    return getScansPerWeek(tier);
  }, [tier]);

  const unlimitedScans = useMemo(() => {
    return hasUnlimitedScans(tier);
  }, [tier]);

  return {
    tier,
    features,
    checkFeature,
    scansAllowed,
    unlimitedScans,
    isPlus: tier === 'plus',
    isPro: tier === 'pro',
    isElite: tier === 'elite',
  };
};



